import { ReactNode, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/utils/invokeEdgeFunction";
import type { Tables } from "@/integrations/supabase/types";
import { Sentry, isSentryEnabled } from "@/sentry";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export interface SendOfferLetterDialogProps {
  candidate: Tables<"candidates"> & {
    colleges?: { name: string } | null;
    job_positions?: { role_name: string } | null;
    offer_email_sent_at?: string | null;
    final_round_date?: string | null;
    commitment_amount?: string | null;
    offer_validity_date?: string | null;
    document_submission_deadline?: string | null;
  };
  onSent?: () => void;
  trigger?: ReactNode;
}

type RoleType = "internship" | "fulltime";

interface OfferFormData {
  roleType: RoleType;
  finalRoundDate: string;
  joiningDate: string;
  allowance: string;
  conversionAmount: string;
  commitmentAmount: string;
  offerValidityDate: string;
  documentSubmissionDeadline: string;
  roleNameOverride: string;
  senderEmail: string;
  senderName: string;
}

const DEFAULT_SENDER_EMAIL = "momin@enacton.com";
const DEFAULT_SENDER_NAME = "Momin Kaloo";

export function SendOfferLetterDialog({
  candidate,
  onSent,
  trigger,
}: SendOfferLetterDialogProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const candidateExtra = candidate as unknown as {
    role_name_override?: string | null;
    offer_role_type?: string | null;
    allowance?: string | null;
    conversion_amount?: string | null;
  };

  const [formData, setFormData] = useState<OfferFormData>({
    roleType: "internship",
    finalRoundDate: "",
    joiningDate: "",
    allowance: "",
    conversionAmount: "",
    commitmentAmount: "50,000",
    offerValidityDate: "",
    documentSubmissionDeadline: "",
    roleNameOverride: "",
    senderEmail: DEFAULT_SENDER_EMAIL,
    senderName: DEFAULT_SENDER_NAME,
  });

  // Initialize form data when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        roleType: "internship",
        finalRoundDate: candidate.final_round_date || "",
        joiningDate: candidate.joining_date || "",
        allowance: candidateExtra.allowance || "",
        conversionAmount: candidateExtra.conversion_amount || "",
        commitmentAmount: candidate.commitment_amount || "50,000",
        offerValidityDate: candidate.offer_validity_date || "",
        documentSubmissionDeadline:
          candidate.document_submission_deadline || "",
        roleNameOverride: candidateExtra.role_name_override || "",
        senderEmail: DEFAULT_SENDER_EMAIL,
        senderName: DEFAULT_SENDER_NAME,
      });
    }
  }, [open, candidate]);

  const handleInputChange = (field: keyof OfferFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("candidates")
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update({
          final_round_date: formData.finalRoundDate || null,
          joining_date: formData.joiningDate || null,
          offer_role_type: formData.roleType,
          allowance: formData.allowance || null,
          conversion_amount: formData.conversionAmount || null,
          commitment_amount: formData.commitmentAmount || null,
          offer_validity_date: formData.offerValidityDate || null,
          document_submission_deadline:
            formData.documentSubmissionDeadline || null,
          updated_at: new Date().toISOString(),
        } as any) // eslint-disable-line @typescript-eslint/no-explicit-any
        .eq("id", candidate.id);

      if (error) throw error;
      toast({ title: "Offer details saved successfully." });
      onSent?.();
    } catch (err) {
      console.error("Save offer details error:", err);
      if (isSentryEnabled) Sentry.captureException(err);
      toast({
        title: "Error",
        description: "Failed to save offer details",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSend = async () => {
    setIsSending(true);
    try {
      // Save first to ensure latest data is in DB
      const { error: saveError } = await supabase
        .from("candidates")
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update({
          final_round_date: formData.finalRoundDate || null,
          joining_date: formData.joiningDate || null,
          offer_role_type: formData.roleType,
          allowance: formData.allowance || null,
          conversion_amount: formData.conversionAmount || null,
          commitment_amount: formData.commitmentAmount || null,
          offer_validity_date: formData.offerValidityDate || null,
          document_submission_deadline:
            formData.documentSubmissionDeadline || null,
          role_name_override: formData.roleNameOverride || null,
          updated_at: new Date().toISOString(),
        } as any) // eslint-disable-line @typescript-eslint/no-explicit-any
        .eq("id", candidate.id);

      if (saveError) throw saveError;

      await invokeEdgeFunction("send-offer-letter", {
        body: {
          candidateId: candidate.id,
          roleType: formData.roleType,
          senderEmail: formData.senderEmail || DEFAULT_SENDER_EMAIL,
          senderName: formData.senderName || DEFAULT_SENDER_NAME,
        },
      });
      toast({ title: "Offer letter sent successfully." });
      setOpen(false);
      setConfirmOpen(false);
      onSent?.();
    } catch (err) {
      console.error("Send offer letter error:", err);
      if (isSentryEnabled) Sentry.captureException(err);
      toast({
        title: "Error",
        description: "Failed to send offer letter",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const isOfferEmailSent = Boolean(candidate.offer_email_sent_at);
  const sentLabel = isOfferEmailSent
    ? `Sent on ${new Date(candidate.offer_email_sent_at!).toLocaleString()}`
    : "Not sent";

  const allowanceLabel =
    formData.roleType === "fulltime" ? "Monthly Salary (Rs.)" : "Monthly Stipend (Rs.)";

  const isInternship = formData.roleType === "internship";

  const isFormValid =
    formData.finalRoundDate &&
    formData.joiningDate &&
    formData.allowance &&
    (!isInternship || formData.conversionAmount) &&
    formData.commitmentAmount &&
    formData.offerValidityDate &&
    formData.documentSubmissionDeadline;

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          {trigger ?? <Button variant="default">Send Offer Letter</Button>}
        </DialogTrigger>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Offer Letter Details</DialogTitle>
            <DialogDescription>
              Fill in the offer details for {candidate.full_name}. You can save
              now and send later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="final-round-date">Final Round Date</Label>
                <DatePickerInput
                  id="final-round-date"
                  value={formData.finalRoundDate}
                  onChange={(v) => handleInputChange("finalRoundDate", v)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="joining-date">Joining Date</Label>
                <DatePickerInput
                  id="joining-date"
                  value={formData.joiningDate}
                  onChange={(v) => handleInputChange("joiningDate", v)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="allowance">{allowanceLabel}</Label>
                <Input
                  id="allowance"
                  type="text"
                  placeholder="e.g. 15,000"
                  value={formData.allowance}
                  onChange={(e) =>
                    handleInputChange("allowance", e.target.value)
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="commitment-amount">Commitment Amount (Rs.)</Label>
                <Input
                  id="commitment-amount"
                  type="text"
                  placeholder="50,000"
                  value={formData.commitmentAmount}
                  onChange={(e) =>
                    handleInputChange("commitmentAmount", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="conversion-amount">
                Post-Internship Conversion CTC
              </Label>
              <Input
                id="conversion-amount"
                type="text"
                placeholder="e.g. 3.5 LPA - 4.5 LPA"
                value={formData.conversionAmount}
                onChange={(e) =>
                  handleInputChange("conversionAmount", e.target.value)
                }
              />
              <p className="text-xs text-muted-foreground">
                Expected full-time CTC range after successful completion of the
                internship.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="offer-validity-date">Offer Valid Until</Label>
                <DatePickerInput
                  id="offer-validity-date"
                  value={formData.offerValidityDate}
                  onChange={(v) => handleInputChange("offerValidityDate", v)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="document-deadline">Document Deadline</Label>
                <DatePickerInput
                  id="document-deadline"
                  value={formData.documentSubmissionDeadline}
                  onChange={(v) =>
                    handleInputChange("documentSubmissionDeadline", v)
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role-name-override">
                Role Name Override{" "}
                <span className="text-muted-foreground font-normal">(Optional)</span>
              </Label>
              <Input
                id="role-name-override"
                type="text"
                placeholder={
                  candidate.job_positions?.role_name || "Enter custom role name"
                }
                value={formData.roleNameOverride}
                onChange={(e) =>
                  handleInputChange("roleNameOverride", e.target.value)
                }
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use default:{" "}
                {candidate.job_positions?.role_name}
              </p>
            </div>

            {/* Sender details */}
            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-medium text-foreground">Sender Details</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sender-name">Sender Name</Label>
                  <Input
                    id="sender-name"
                    type="text"
                    value={formData.senderName}
                    onChange={(e) =>
                      handleInputChange("senderName", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sender-email">Sender Email</Label>
                  <Input
                    id="sender-email"
                    type="email"
                    value={formData.senderEmail}
                    onChange={(e) =>
                      handleInputChange("senderEmail", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            <div className="text-sm text-muted-foreground pt-2 border-t">
              Offer email status:{" "}
              <span className="font-medium">{sentLabel}</span>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={isSaving || isSending}
                className="flex-1"
              >
                {isSaving ? "Saving..." : "Save Details"}
              </Button>
              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={
                  isSending ||
                  isSaving ||
                  !candidate.email ||
                  !isFormValid
                }
                className="flex-1"
              >
                {isSending ? "Sending..." : "Send Offer Mail"}
              </Button>
            </div>
            {!isFormValid && (
              <p className="text-xs text-muted-foreground">
                Please fill all fields before sending.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send offer mail now?</AlertDialogTitle>
            <AlertDialogDescription>
              This will email the{" "}
              {formData.roleType === "fulltime" ? "full-time" : "internship"}{" "}
              offer letter to {candidate.full_name} at {candidate.email}
              {" "}from{" "}
              <span className="font-medium">
                {formData.senderName} &lt;{formData.senderEmail}&gt;
              </span>
              .
              {isOfferEmailSent && (
                <span className="block mt-2 text-yellow-600">
                  Note: An offer email was already sent previously.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSend} disabled={isSending}>
              {isSending ? "Sending..." : "Confirm Send"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
